import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import moment from "moment";
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { UnixToCentralTimePipe } from 'src/app/_helpers/pipes/unix-to-central-date.pipe';
import { IAnalysis } from 'src/app/_interfaces/IAnalysis';
import { AnalysisFacadeService } from 'src/app/_services/analysis/analysis-facade.service';
import { AnalysisNote, AnalysisNoteType, AnalysisNotesService, ImmutableAnalysisNoteTypes, Note } from '../analysis-notes.service';
import { AnalysisNotesDeleteDialogComponent } from '../rte-existing-notes/a-notes-delete-dialog/a-notes-delete-dialog.component';

@Component({
    selector: 'wfdss-rte-analysis-notes',
    templateUrl: './rte-analysis-notes.component.html',
    styleUrls: ['./rte-analysis-notes.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RTEAnalysisNotesComponent implements OnInit {
  analysis$: Observable<IAnalysis>;
  analysis: IAnalysis  
  analysisRestricted: boolean = false
  showCreateButton: boolean = false  
	private destroyRef = inject(DestroyRef)
  notesList: AnalysisNote[]
  selectedNote: AnalysisNote
  _cachedNote: BehaviorSubject<AnalysisNote> = new BehaviorSubject<AnalysisNote>(null)
  noteReadonly: boolean
  saveButtonText: string = "Save New Note"
  editingExistingNote: boolean = false
  _resetCreate = new Subject<void>() 
  currentNoteLabel = "Creating a new note"
  createNoteTypeDroprownOptions: {type: AnalysisNoteType, disabled: boolean}[]
  formGroupForNoteInEdit;
  private dialog = inject(MatDialog);

  constructor(private analysisNoteSvc: AnalysisNotesService,
              protected analysisFacadeService: AnalysisFacadeService,
              private cdr: ChangeDetectorRef){}

  ngOnInit(): void {    
    this.analysisNoteSvc.notes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((notes)=>{
      this.notesList = notes
      this.cdr.detectChanges();
    })
    this.analysisNoteSvc.dropdownTypes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((types)=>{
      this.createNoteTypeDroprownOptions = types
    })
    this.analysisFacadeService.analysisRestricted$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((restricted: boolean)=>{
      this.analysisRestricted = restricted
    })
    // applicable when analyst/support accepts analysis
    this.analysisFacadeService.analysis$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((analysis)=>{
      this.analysis = analysis
      this.noteReadonly = this.analysis?.status == 'Requested' || this.analysisFacadeService.canViewOrEditAnalysisInd === 'view';
    })
    
    this.getNotes()
  }
  saveNote(analysisNote: AnalysisNote){
    this.analysisNoteSvc.saveNote(analysisNote).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((newNote)=>{
      this.setEditor()
      this.getNotes()
      this.cdr.detectChanges()
    })
  }
  getNotes(){
    this.analysisNoteSvc.refreshNotes().pipe(take(1)).subscribe(()=>{
      this.cdr.detectChanges()
    })
  }
  onClickExistingNoteFromTableRow(note: AnalysisNote){
    this.setEditor(note)
    this.cdr.detectChanges()
  }
  onClickCreateNewNote(){
    this.setEditor()
    this.cdr.detectChanges()
  }
  onClickCancelNewNoteEdit(){
    this.setEditor()
    this.cdr.detectChanges()
  }
  onCancelExistingNoteEdit(){ 
    this.selectedNote = null // without setting the note to null the changes are not detected by the child "existing note" component 
    this.selectedNote = this._cachedNote.value
    this.cdr.detectChanges() // current working theory is that the underlying "note" input in existing note edit doesn't actually change. 
  }
  onClickDeleteNoteFromTableButton(note: AnalysisNote) {    
    this.dialog.open(AnalysisNotesDeleteDialogComponent, {data: note}).afterClosed().subscribe((result)=>{
      if(result == "delete"){
        this.deleteAnalysisNote(note);
      }
    })
  }
  deleteAnalysisNote(note: AnalysisNote) {
    this.analysisNoteSvc.deleteNote(note).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      (result)=>{
        this.getNotes()
        console.log("delete note result", result)
      }
    )
  }


  /**
   * @description Populates or clears the editor contents and associated state variables.
   * @param note Optional. Inclusion or omission determines whether the 'edit-existing' or 'create' implementation is used.
    */
  private setEditor(note?: AnalysisNote){
    if(note){
      if(this.checkNoteForLock(note)){
        this.noteReadonly = true
        this.editingExistingNote = true
        this.currentNoteLabel = `Viewing '${note.title}'`
        this.selectedNote = note
        this.showCreateButton = true
      } else {
        this.noteReadonly = false
        this.editingExistingNote = true
        this.currentNoteLabel = `Editing '${note.title}'`
        this.saveButtonText = "Save Edit"
        this.selectedNote = note
        this._cachedNote.next(note)
        this.showCreateButton = true
      }
    } else {
      this.showCreateButton = false
      this.editingExistingNote = false
      this.currentNoteLabel = "Creating a new note"
      this.saveButtonText = "Save New Note"
      this.selectedNote = null
      this._resetCreate.next()
    }
    this.cdr.detectChanges()
  }

  private checkNoteForLock(note: Note){
    if(ImmutableAnalysisNoteTypes.includes(note.type as AnalysisNoteType)){
      return true
    }
    if( this.analysisFacadeService.canViewOrEditAnalysisInd === 'view'
        || this.analysis?.status == 'Requested'
    ){
      return true
    } else {
      return false
    }
  }
  analysisNoteIdAccessor = (note: AnalysisNote) =>{
    if(note?.analysisNotesId){
      return {analysisNotesId: note.analysisNotesId}
    } else {
      return null
    }
  }
  sortingDataAccessor = (data: any, prop, forExport?: false) => {
    switch (prop) {
      case 'Title' :
        return data?.title?.toLowerCase();
      case 'Type' :
        return data?.type?.toLowerCase();
      case 'Created By' :
        return data?.createdByUser?.toLowerCase();
      case 'Status' :
        return data?.status?.toLowerCase();
      case 'Created Date/Time(CT)' :
        return data?.createdDate;
    }
  };
  columnMetaInfo = [
    {
      propertyAccessor: (prop: Note) => {return prop.title},
      columnName: "Title",
      // cellClick
    },
    {
      propertyAccessor: (prop: Note) => {return prop.type},
      columnName: "Type"
    },
    {
      propertyAccessor: (prop: Note) => {return prop.createdByUser},
      columnName: "Created By"
    },
    {
      propertyAccessor: (prop: Note) => {
        // return prop.crea
        const createdDateTime = new UnixToCentralTimePipe().transform(prop?.createdDate);
        return createdDateTime ? moment(createdDateTime).format("MM/DD/YY HH:mm") : null;
      },
      columnName: "Created Date/Time(CT)"
    },
    {
      
      propertyAccessor: (prop: Note) => {
        // return prop.crea
        const createdDatTime = new UnixToCentralTimePipe().transform(prop?.createdDate);
        return createdDatTime
      },
      columnName: "delete",
      cellClick: {
        isDisabled: (note)=>{
          return this.checkNoteForLock(note)
        },
        onCellClick: (s: AnalysisNote)=>{ this.onClickDeleteNoteFromTableButton(s)},
        icon: "delete",
        iconAriaLabel: "icon of a trashcan",
        buttonLabel: "delete",
      }
    },
  ]

}
